import {Logger, PubSub} from '@mamdev/cad.static-next-lib';
import Viewport from './viewport';

// Selector for all focusable elements
const FOCUSABLE_ELEMENT_SELECTOR = 'a[href], area[href], input:enabled:not([type="hidden"]), select:enabled, textarea:enabled, button:enabled:not([disabled]), iframe, object, [tabindex="0"], [contenteditable]';

/**
 * Accessibility service for managing focus trapping and keyboard navigation
 */
export class Accessibility {
  private trappedFocusElement: HTMLElement | null = null;
  private firstFocusableElement: HTMLElement | null = null;
  private lastFocusableElement: HTMLElement | null = null;
  private previousFocusedElement: HTMLElement | null = null;
  private usingKeyboard = false;
  private initialized = false;

  private readonly focusHandler = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      if (e.shiftKey && document.activeElement === this.firstFocusableElement) {
        e.preventDefault();
        this.lastFocusableElement?.focus();
      } else if (!e.shiftKey && document.activeElement === this.lastFocusableElement) {
        e.preventDefault();
        this.firstFocusableElement?.focus();
      }
      PubSub.publish('accessibility:focusChanged');
    } else if (e.key === 'Escape') {
      PubSub.publish('accessibility:escape', this.trappedFocusElement);
    }
  };

  private log(message: string): void {
    Logger.log('accessibility', message);
  }

  private logError(message: unknown): void {
    Logger.log('accessibility', String(message), false, console.error);
  }

  private logWarning(message: string): void {
    Logger.log('accessibility', message, false, console.warn);
  }

  // Public getters for testing
  public getTrappedFocusElement(): HTMLElement | null {
    return this.trappedFocusElement;
  }

  public getFirstFocusableElement(): HTMLElement | null {
    return this.firstFocusableElement;
  }

  public getLastFocusableElement(): HTMLElement | null {
    return this.lastFocusableElement;
  }

  public getUsingKeyboard(): boolean {
    return this.usingKeyboard;
  }

  public getInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Initializes the accessibility service
   */
  public init(): void {
    try {
      if (this.initialized) return;

      this.log('init');

      if (Viewport.isDesktop()) {
        document.addEventListener('keydown', e => {
          if (e.key === 'Tab') {
            this.showFocus();
          }
        });

        document.addEventListener('mousedown', () => {
          if (this.usingKeyboard) {
            this.usingKeyboard = false;
            document.body.classList.remove('show-focus-indicator');
            PubSub.publish('accessibility:keyboard-end');
          }
        });
      }

      this.initialized = true;
    } catch (e) {
      this.logError(e);
    }
  }

  /**
   * Traps focus within the specified element
   * @param element The element to trap focus within
   * @param focusElement Optional element to receive initial focus (defaults to first focusable)
   */
  public trapFocus(element: HTMLElement | null | undefined, focusElement?: HTMLElement | null): void {
    try {
      if (!element) {
        this.logWarning('element is undefined or null');
        return;
      }

      if (this.trappedFocusElement === element) return;

      const focusableElements = element.querySelectorAll<HTMLElement>(FOCUSABLE_ELEMENT_SELECTOR);
      if (focusableElements.length < 1) {
        this.logWarning('No focusable elements found in target');
        return;
      }

      if (this.trappedFocusElement) {
        this.releaseFocus();
      }

      this.previousFocusedElement = document.activeElement as HTMLElement;
      this.trappedFocusElement = element;
      this.firstFocusableElement = focusableElements[0];
      this.lastFocusableElement = focusableElements[focusableElements.length - 1];

      const elementToFocus = focusElement ?? this.firstFocusableElement;
      elementToFocus?.focus();
      element.addEventListener('keydown', this.focusHandler);

      this.log('focus trapped');
    } catch (e) {
      this.logError(e);
    }
  }

  /**
   * Releases the trapped focus and restores it to the previous element
   */
  public releaseFocus(): void {
    try {
      if (!this.trappedFocusElement) return;

      this.trappedFocusElement.removeEventListener('keydown', this.focusHandler);

      this.trappedFocusElement = null;
      this.firstFocusableElement = null;
      this.lastFocusableElement = null;

      if (this.previousFocusedElement) {
        this.previousFocusedElement.focus();
      }

      this.log('focus released');
    } catch (e) {
      this.logError(e);
    }
  }

  /**
   * Shows focus indicators for keyboard navigation
   */
  public showFocus(): void {
    if (!this.usingKeyboard) {
      this.usingKeyboard = true;
      document.body.classList.add('show-focus-indicator');
      PubSub.publish('accessibility:keyboard-start');
    }
  }
}

export default new Accessibility();
